/**
 * CMF — Chaikin Money Flow.
 *
 * Migrado do Silverlight (GraficoDePrata/Formulas/FMLOscilators.cs, classe
 * FML.CMF). Painel próprio, 1 série (COLORSTICK no engine):
 *
 *   D   = H − L
 *   AD  = IF(D == 0, 0, ((C − L) − (H − C)) / D · VOL)
 *   MV  = MA(VOL, N)
 *   CMF = IF(MV == 0, 0, MA(AD, N) / MV)
 *
 * Os dois guards do engine (D==0 e MV==0) estão portados verbatim — é o que
 * evita NaN em barras "flat" e em janelas sem volume. Mesma fórmula do
 * `calculateChaikinMoneyFlow` do ChartIQ, então este calculator fecha os dois
 * eixos de paridade. Não confundir com `twiggsMF` (true range + Wilder).
 * Paridade provada em __tests__/chaikinMoneyFlow.silverlight.test.js.
 */
import { sub, div, mul, ma, eq, iff } from "./silverlightDsl";
import { ChaikinMoneyFlow as defaultOptions } from "./defaultOptionsForComputation";

export default function () {
  let options = defaultOptions;

  function calculator(data) {
    const { period } = options;
    const num = (v) => (Number.isFinite(v) ? v : NaN);
    const high = data.map((d) => num(d.high));
    const low = data.map((d) => num(d.low));
    const close = data.map((d) => num(d.close));
    // d.volume pode já ter sido processado pelo indicador Volume no mesmo
    // pipeline (applyIndicators roda em ordem): vira { volume, direction }.
    const vol = data.map((d) => {
      const raw = d.volume;
      return num(
        raw !== null && typeof raw === "object"
          ? Number(raw.volume)
          : Number(raw),
      );
    });

    const d = sub(high, low);
    const clv = sub(sub(close, low), sub(high, close));
    const ad = iff(eq(d, 0), 0, mul(div(clv, d), vol));
    const mv = ma(vol, period);
    const cmf = iff(eq(mv, 0), 0, div(ma(ad, period), mv));

    return cmf.map((v) =>
      Number.isFinite(v)
        ? { value: v, direction: v > 0 ? "up" : "down" }
        : undefined,
    );
  }

  calculator.undefinedLength = function () {
    const { period } = options;
    return Math.max(0, period - 1);
  };

  calculator.options = function (x) {
    if (!arguments.length) {
      return options;
    }
    options = { ...defaultOptions, ...x };
    return calculator;
  };

  return calculator;
}
